import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { DateTime } from '~/components/Form';
import { PesquisaTabela } from '~/components/Form/v2';
import { parse } from 'date-fns';
import { t } from '~/helpers';
import { compareIsAfterDates } from '~/helpers/dateTime';
import { validation, validationErrors } from '~/helpers/yupValidations';
import { storeData } from '~/store/modules/alteracaoCentroCusto/actions';

export default function AlteracaoCentroCustoForm({ reference, create }) {
  const dispatch = useDispatch();
  const params = useSelector((s) => s.alteracaoCentroCusto.params);
  const veiculoHeader = useMemo(
    () => [
      {
        id: 'idveiculo',
        label: t('s.menu.cadastro.veiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'placaveiculo',
        label: t('s.cadastro.veiculo.placaveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.cadastro.veiculo.idmodeloveiculo'),
        filter: { type: 'text' },
      },
    ],
    []
  );
  const centroHeader = useMemo(
    () => [
      {
        id: 'idcentrocusto',
        label: t('s.cadastro.centrocusto.idcentrocusto'),
        filter: { type: 'text' },
      },
      {
        id: 'nomecentrocusto',
        label: t('s.cadastro.centrocusto.nomecentrocusto'),
        filter: { type: 'text' },
      },
    ],
    []
  );
  const submit = useCallback(
    async (data) => {
      try {
        reference.current.setErrors({});

        const schema = Yup.object().shape({
          datahorainicialalteracaocentrocusto: Yup.mixed()
            .transform((value) =>
              value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
            )
            .required(t('s.geral.validacao.campoobrigatorio')),
          datahorafinalalteracaocentrocusto: Yup.mixed()
            .transform((value) =>
              value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
            )
            .required(t('s.geral.validacao.campoobrigatorio'))
            .test(
              'datahorafinalalteracaocentrocusto',
              t('s.operacao.descarganf.datahorainicialmaiordatahorafinal'),
              () =>
                !compareIsAfterDates({
                  current: data.datahorainicialalteracaocentrocusto,
                  compare: data.datahorafinalalteracaocentrocusto,
                })
            ),
          idveiculo: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          idcentrocusto: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
        });

        await validation(schema, data);

        if (create) dispatch(storeData(data, params));
      } catch (error) {
        validationErrors(error, reference);
      }
    },
    [create, dispatch, params, reference]
  );
  return (
    <Form ref={reference} onSubmit={submit}>
      <DateTime
        time
        name="datahorainicialalteracaocentrocusto"
        label={t('s.geral.opcoes.datahorainicial')}
      />
      <DateTime
        time
        name="datahorafinalalteracaocentrocusto"
        label={t('s.geral.opcoes.datahorafinal')}
      />
      <PesquisaTabela
        name="idveiculo"
        label={t('s.menu.cadastro.veiculo')}
        primary="idveiculo"
        secondary="placaveiculo"
        url="veiculos"
        header={veiculoHeader}
      />
      <PesquisaTabela
        name="idcentrocusto"
        label={t('s.menu.cadastro.veiculo.centrocusto')}
        primary="idcentrocusto"
        secondary="nomecentrocusto"
        url="centroscustos"
        header={centroHeader}
        query={{
          wherefield: ['statuscentrocusto'],
          valuewherefield: ['L'],
          typewherefield: ['EQUALS'],
        }}
      />
    </Form>
  );
}
AlteracaoCentroCustoForm.propTypes = {
  reference: PropTypes.shape({ current: PropTypes.object }).isRequired,
  create: PropTypes.bool.isRequired,
};
